import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'designSystem/lib/utils'
import { Slot } from 'radix-ui'
import * as React from 'react'

function ItemGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      role='list'
      data-slot='item-group'
      className={cn(
        'gap-4 has-data-[size=sm]:gap-2.5 has-data-[size=xs]:gap-2 has-data-[size=compact]:gap-2 group/item-group flex w-full flex-col',
        className
      )}
      {...props}
    />
  )
}

const itemVariants = cva(
  '[a]:hover:bg-muted rounded-md border text-xs/relaxed w-full min-w-0 max-w-full group/item focus-visible:border-ring focus-visible:ring-ring/50 flex items-center flex-wrap text-left outline-none transition-colors duration-100 focus-visible:ring-[3px] [a]:transition-colors',
  {
    variants: {
      variant: {
        default: 'border-transparent',
        outline: 'border-border',
        muted: 'bg-muted/50 border-transparent',
        surface:
          'border-border bg-accent hover:bg-input dark:hover:bg-muted [a&]:hover:bg-input dark:[a&]:hover:bg-muted',
        surfaceActive:
          'border-ring bg-input dark:bg-muted hover:bg-input dark:hover:bg-muted [a&]:hover:bg-input dark:[a&]:hover:bg-muted',
        surfaceMuted: 'border-border bg-muted hover:bg-muted',
      },
      hoverHighlight: {
        true: 'hover:!bg-muted',
        false: '',
      },
      triggerFocusRing: {
        true: 'has-[[data-slot=item-trigger]:focus-visible]:border-ring has-[[data-slot=item-trigger]:focus-visible]:ring-ring/50 has-[[data-slot=item-trigger]:focus-visible]:ring-[3px]',
        false: '',
      },
      disabled: {
        true: '!cursor-not-allowed opacity-[0.55] hover:!bg-transparent',
        false: '',
      },
      size: {
        default: 'gap-2.5 px-3 py-2.5',
        lg: 'h-auto min-h-0 shrink-0 gap-2.5 flex-nowrap overflow-hidden rounded-xl p-3 text-sm leading-5',
        compact:
          'h-auto min-h-0 shrink-0 gap-1.5 flex-nowrap overflow-hidden rounded-xl p-2 text-xs leading-4',
        sm: 'gap-2.5 px-3 py-2.5',
        xs: 'gap-2.5 px-2.5 py-2 in-data-[slot=dropdown-menu-content]:p-0',
        md: 'gap-2.5 px-3 py-5',
        tile: 'relative aspect-square w-[120px] shrink-0 flex-col items-stretch flex-nowrap gap-0 overflow-hidden rounded-xl p-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      hoverHighlight: false,
      triggerFocusRing: false,
      disabled: false,
    },
  }
)

function Item({
  className,
  variant = 'default',
  size = 'default',
  hoverHighlight = false,
  triggerFocusRing = false,
  disabled = false,
  asChild = false,
  ...props
}: React.ComponentProps<'div'> &
  VariantProps<typeof itemVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'div'
  return (
    <Comp
      data-slot='item'
      data-variant={variant}
      data-size={size}
      data-disabled={disabled ? true : undefined}
      className={cn(
        itemVariants({
          variant,
          size,
          hoverHighlight,
          triggerFocusRing,
          disabled,
          className,
        })
      )}
      {...props}
    />
  )
}

function ItemTrigger({ className, ...props }: React.ComponentProps<'button'>) {
  return (
    <button
      data-slot='item-trigger'
      className={cn(
        'flex w-full min-w-0 flex-1 cursor-pointer items-center self-stretch gap-2.5 overflow-hidden border-0 bg-transparent p-0 text-left text-inherit [font:inherit] focus-visible:outline-none group-data-[size=compact]/item:gap-1.5',
        className
      )}
      {...props}
    />
  )
}

const itemMediaVariants = cva(
  'gap-2 group-has-data-[slot=item-description]/item:translate-y-0.5 group-has-data-[slot=item-description]/item:self-start flex shrink-0 items-center justify-center [&_svg]:pointer-events-none',
  {
    variants: {
      align: {
        default: '',
        center: 'self-center! translate-y-0!',
      },
      size: {
        default: '',
        lg: 'size-12!',
      },
      variant: {
        default: 'bg-transparent',
        icon: "[&_svg:not([class*='size-'])]:size-4",
        image:
          'size-8 overflow-hidden rounded-sm group-data-[size=sm]/item:size-8 group-data-[size=xs]/item:size-6 [&_img]:size-full [&_img]:object-cover',
        thumbnail:
          'w-24 shrink-0 self-center! translate-y-0! overflow-visible rounded-[6px] bg-transparent group-data-[size=compact]/item:w-16',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function ItemMedia({
  className,
  variant = 'default',
  align = 'default',
  size = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemMediaVariants>) {
  return (
    <div
      data-slot='item-media'
      data-variant={variant}
      className={cn(itemMediaVariants({ variant, align, size, className }))}
      {...props}
    />
  )
}

function ItemMediaFrame({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='item-media-frame'
      className={cn(
        'flex h-full w-full items-center justify-center overflow-hidden rounded-[1px]',
        className
      )}
      {...props}
    />
  )
}

const itemContentVariants = cva(
  'gap-1 group-data-[size=xs]/item:gap-0.5 flex flex-1 flex-col [&+[data-slot=item-content]]:flex-none',
  {
    variants: {
      variant: {
        default: '',
        document:
          'h-full min-w-0 max-w-full justify-center gap-2 self-stretch overflow-hidden group-data-[size=compact]/item:gap-1.5 group-data-[size=lg]/item:justify-start',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function ItemContent({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemContentVariants>) {
  return (
    <div
      data-slot='item-content'
      className={cn(itemContentVariants({ variant, className }))}
      {...props}
    />
  )
}

const itemTitleVariants = cva(
  'gap-2 text-xs leading-tight font-medium underline-offset-4 line-clamp-1 flex w-fit items-center',
  {
    variants: {
      variant: {
        default: '',
        document:
          'h-5 w-full min-w-0 max-w-full shrink-0 gap-0 overflow-hidden text-sm leading-5 [display:flex] group-data-[size=compact]/item:h-4 group-data-[size=compact]/item:text-xs group-data-[size=compact]/item:leading-4',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function ItemTitle({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemTitleVariants>) {
  return (
    <div
      data-slot='item-title'
      className={cn(itemTitleVariants({ variant, className }))}
      {...props}
    />
  )
}

const itemTitleTextVariants = cva('block whitespace-nowrap', {
  variants: {
    tone: {
      default: 'text-foreground',
      muted: 'text-muted-foreground',
    },
    spacing: {
      default: '',
      leading: 'ml-1.5',
    },
    truncate: {
      true: 'min-w-0 overflow-hidden text-ellipsis',
      false: 'shrink-0',
    },
    grow: {
      true: 'flex-1',
      false: '',
    },
  },
  defaultVariants: {
    tone: 'default',
    spacing: 'default',
    truncate: true,
    grow: false,
  },
})

function ItemTitleText({
  className,
  tone = 'default',
  spacing = 'default',
  truncate = true,
  grow = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof itemTitleTextVariants>) {
  return (
    <span
      data-slot='item-title-text'
      className={cn(
        itemTitleTextVariants({ tone, spacing, truncate, grow, className })
      )}
      {...props}
    />
  )
}

const itemDescriptionVariants = cva(
  'text-left text-xxs text-muted-foreground [&>a:hover]:text-primary line-clamp-2 font-normal [&>a]:underline [&>a]:underline-offset-4',
  {
    variants: {
      variant: {
        default: '',
        document: 'line-clamp-3 text-sm leading-5',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

function ItemDescription({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'p'> & VariantProps<typeof itemDescriptionVariants>) {
  return (
    <p
      data-slot='item-description'
      className={cn(itemDescriptionVariants({ variant, className }))}
      {...props}
    />
  )
}

function ItemSnippet({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='item-snippet'
      className={cn(
        'line-clamp-3 overflow-hidden text-left text-sm leading-5 font-normal text-muted-foreground group-data-[size=compact]/item:line-clamp-2 group-data-[size=compact]/item:text-xs group-data-[size=compact]/item:leading-4',
        className
      )}
      {...props}
    />
  )
}

const itemActionsVariants = cva('gap-2 flex items-center', {
  variants: {
    variant: {
      default: '',
      trailing: 'shrink-0',
      titleTrailing: 'shrink-0 self-start gap-1',
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

function ItemActions({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemActionsVariants>) {
  return (
    <div
      data-slot='item-actions'
      className={cn(itemActionsVariants({ variant, className }))}
      {...props}
    />
  )
}

const itemMetaRowVariants = cva('flex min-w-0 items-center', {
  variants: {
    gap: {
      default: 'gap-1.5',
      relaxed: 'gap-3',
    },
  },
  defaultVariants: {
    gap: 'default',
  },
})

function ItemMetaRow({
  className,
  gap = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof itemMetaRowVariants>) {
  return (
    <div
      data-slot='item-meta-row'
      className={cn(itemMetaRowVariants({ gap, className }))}
      {...props}
    />
  )
}

function ItemMetaField({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='item-meta-field'
      className={cn(
        'flex min-w-0 items-center gap-1 text-sm leading-5 text-foreground group-data-[size=compact]/item:text-xs group-data-[size=compact]/item:leading-4 [&_svg]:pointer-events-none [&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground group-data-[size=compact]/item:[&_svg]:size-3',
        className
      )}
      {...props}
    />
  )
}

function ItemMetaFieldText({
  className,
  ...props
}: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot='item-meta-field-text'
      className={cn('truncate', className)}
      {...props}
    />
  )
}

function ItemTagRow({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='item-tag-row'
      className={cn('flex min-w-0 flex-wrap items-start gap-1', className)}
      {...props}
    />
  )
}

function ItemMeta({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='item-meta'
      className={cn('min-w-0 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

export {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemMediaFrame,
  ItemMeta,
  ItemMetaField,
  ItemMetaFieldText,
  ItemMetaRow,
  ItemSnippet,
  ItemTagRow,
  ItemTitle,
  ItemTitleText,
  ItemTrigger,
}
